// Console patch for the FS App Manager frontend.
//
// Every console.log / .info / .warn / .error / .debug call anywhere in the
// dashboard now prints with:
//
//   [<ISO timestamp>] [<LEVEL>] [<file>:<function>] <original message>
//
// In Chrome DevTools that becomes a single greppable line per event. The
// original message is preserved verbatim so existing grep targets like
// `[dashboard-tick]`, `[auto-reconcile]`, `[audit]` still match — we only
// DECORATE.
//
// Loaded once at startup from main.tsx via `import './utils/logger'`. No
// component-level call sites need to change.

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
const LEVELS: LogLevel[] = ['log', 'info', 'warn', 'error', 'debug'];

const originalConsole = {
  log:   console.log.bind(console),
  info:  console.info.bind(console),
  warn:  console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
};

// Vite serves source files under `/src/<path>?t=…` in dev and bundles them
// in prod. The dev-time form is the more useful one because it gives us the
// component name; in prod we still get a sensible file/line.
function parseCaller(): { file: string; fn: string } {
  const raw = new Error().stack || '';
  const lines = raw.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] || '';
    if (line.includes('utils/logger')) continue; // skip our own frames
    const m =
      line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/) ||
      line.match(/at\s+(.+?):(\d+):(\d+)/);
    if (!m) continue;
    const fn = m.length === 5 ? m[1] : '<anonymous>';
    const fullPath = m.length === 5 ? m[2] : m[1];
    const after = fullPath.split(/[/\\]src[/\\]/).pop() || fullPath;
    // Drop the `?t=<timestamp>` Vite dev-mode suffix and any source-map
    // markers so the prefix stays compact.
    const file = after.split('?')[0];
    return { file, fn };
  }
  return { file: 'frontend', fn: '<anonymous>' };
}

function isMeaningful(args: unknown[]): boolean {
  if (args.length === 0) return false;
  if (args.length === 1) {
    const a = args[0];
    if (a == null) return false;
    if (typeof a === 'string' && a.trim() === '') return false;
    if (typeof a === 'object' && !Array.isArray(a) && Object.keys(a as object).length === 0) return false;
    if (Array.isArray(a) && a.length === 0) return false;
  }
  return true;
}

function emit(level: LogLevel, args: unknown[]): void {
  if (!isMeaningful(args)) return;
  const ts = new Date().toISOString();
  const { file, fn } = parseCaller();
  const tag = `[${ts}] [${level.toUpperCase()}] [${file}:${fn}]`;
  originalConsole[level](tag, ...args);
}

LEVELS.forEach((level) => {
  // The double-cast through `unknown` is the documented TS escape hatch for
  // patching `console`, whose typed methods don't satisfy a generic
  // record-of-unknown shape directly.
  (console as unknown as Record<LogLevel, (...args: unknown[]) => void>)[level] = (...args: unknown[]) => emit(level, args);
});

export const rawConsole = originalConsole;
